/**
 * 统一模板管理器
 * 前后端共享，提供一致的模板配置访问接口
 */

import { TemplateConfig, TemplateId, FieldName, FIELD_NAMES, TEMPLATE_IDS } from './types';
import { getSeasonContext, replaceSeasonTokens } from '../utils/season';

const FOUR_SEASONS_TEMPLATE_ID = TEMPLATE_IDS.FOUR_SEASONS_SPREAD;

export class TemplateManager {
  private static templates: Map<string, TemplateConfig> = new Map();
  private static initialized = false;

  /**
   * 获取所有模板配置
   */
  static getAllTemplates(): TemplateConfig[] {
    return Array.from(this.templates.values()).map(config => this.applySeasonalOverrides(config));
  }

  /**
   * 获取所有模板ID列表
   */
  static getAllTemplateIds(): TemplateId[] {
    return Array.from(this.templates.keys());
  }

  /**
   * 获取指定模板配置
   */
  static getConfig(templateId: TemplateId): TemplateConfig | null {
    const config = this.templates.get(templateId) || null;
    if (!config) {
      return null;
    }

    return this.applySeasonalOverrides(config);
  }

  /**
   * 获取模板基本信息
   */
  static getTemplateInfo(templateId: TemplateId): { id: string; name: string; description: string } | null {
    const config = this.getConfig(templateId);
    if (!config) return null;
    
    return {
      id: config.id,
      name: config.name,
      description: config.description
    };
  }

  /**
   * 获取模板默认值
   */
  static getDefaultValues(templateId: TemplateId): Record<string, any> {
    const config = this.getConfig(templateId);
    return config?.fieldDefaults || {};
  }

  /**
   * 获取disabled字段列表（前后端通用）
   */
  static getDisabledFields(templateId: TemplateId): FieldName[] {
    const config = this.getConfig(templateId);
    return config?.disabledFields || [];
  }

  /**
   * 检查指定字段是否disabled
   */
  static isFieldDisabled(templateId: TemplateId, fieldName: FieldName): boolean {
    return this.getDisabledFields(templateId).includes(fieldName);
  }

  /**
   * 获取模板的卡片数量
   */
  static getCardCount(templateId: TemplateId): number | undefined {
    const config = this.getConfig(templateId);
    return config?.cardCount;
  }

  /**
   * 获取模板的生成选项
   */
  static getGenerateOptions(templateId: TemplateId): { normal: boolean; thinking: boolean } | null {
    const config = this.getConfig(templateId);
    return config?.generateOptions || null;
  }

  /**
   * 获取模板UI配置
   */
  static getUIConfig(templateId: TemplateId): TemplateConfig['ui'] | null {
    const config = this.getConfig(templateId);
    return config?.ui || null;
  }

  /**
   * 获取模板特殊分类
   */
  static getSpecialCategory(templateId: TemplateId): string | null {
    const config = this.getConfig(templateId);
    return config?.specialCategory || null;
  }

  /**
   * 检查模板是否属于特殊分类
   */
  static isSpecialCategory(templateId: TemplateId, category: string): boolean {
    const specialCategory = this.getSpecialCategory(templateId);
    return specialCategory === category;
  }

  /**
   * 注册模板配置
   */
  static register(config: TemplateConfig): void {
    const normalizedConfig = this.normalizeConfig(config);
    this.templates.set(normalizedConfig.id, normalizedConfig);
  }

  private static normalizeConfig(config: TemplateConfig): TemplateConfig {
    const disabledFields = Array.from(new Set(config.disabledFields || []));
    const fieldDefaults = { ...config.fieldDefaults };

    if (config.id !== FOUR_SEASONS_TEMPLATE_ID) {
      if (!disabledFields.includes(FIELD_NAMES.CUSTOMER_NAME)) {
        disabledFields.push(FIELD_NAMES.CUSTOMER_NAME);
      }

      if (fieldDefaults.customerName === undefined) {
        fieldDefaults.customerName = '';
      }
    } else {
      const idx = disabledFields.indexOf(FIELD_NAMES.CUSTOMER_NAME);
      if (idx !== -1) {
        disabledFields.splice(idx, 1);
      }

      if (fieldDefaults.customerName === '') {
        delete fieldDefaults.customerName;
      }
    }

    return {
      ...config,
      disabledFields,
      fieldDefaults
    };
  }

  private static applySeasonalOverrides(config: TemplateConfig): TemplateConfig {
    if (config.id !== FOUR_SEASONS_TEMPLATE_ID) {
      return config;
    }

    const seasonContext = getSeasonContext();
    const replaceText = (value?: string) => {
      if (value === undefined) {
        return value;
      }
      return replaceSeasonTokens(value, seasonContext);
    };
    const replaceList = (values?: string[]) => {
      if (!values) {
        return values;
      }
      return values.map(item => replaceText(item) ?? item);
    };

    return {
      ...config,
      name: replaceText(config.name) || config.name,
      description: replaceText(config.description) || config.description,
      fieldDefaults: {
        ...config.fieldDefaults,
        title: replaceText(config.fieldDefaults.title),
        customerQuestion: replaceText(config.fieldDefaults.customerQuestion),
        cardInfos: replaceList(config.fieldDefaults.cardInfos),
        cardPlaceholders: replaceList(config.fieldDefaults.cardPlaceholders)
      }
    };
  }

  /**
   * 批量注册模板配置
   */
  static registerAll(configs: TemplateConfig[]): void {
    configs.forEach(config => this.register(config));
  }

  /**
   * 检查模板是否存在
   */
  static hasTemplate(templateId: TemplateId): boolean {
    return this.templates.has(templateId);
  }

  /**
   * 获取所有具有特定disabled字段的模板ID列表
   * 用于替换后端硬编码数组
   */
  static getTemplatesWithDisabledField(fieldName: FieldName): TemplateId[] {
    return this.getAllTemplates()
      .filter(config => config.disabledFields.includes(fieldName))
      .map(config => config.id);
  }

  /**
   * 获取Title字段disabled的模板列表（后端兼容性方法）
   */
  static getTemplatesWithDisabledTitle(): TemplateId[] {
    return this.getTemplatesWithDisabledField('title');
  }

  /**
   * 获取CustomerQuestion字段disabled的模板列表（后端兼容性方法）
   */
  static getTemplatesWithDisabledCustomerQuestion(): TemplateId[] {
    return this.getTemplatesWithDisabledField('customerQuestion');
  }

  /**
   * 获取CardCount字段disabled的模板列表（后端兼容性方法）
   */
  static getTemplatesWithDisabledCardCount(): TemplateId[] {
    return this.getTemplatesWithDisabledField('cardCount');
  }

  /**
   * 清除所有模板（主要用于测试）
   */
  static clear(): void {
    this.templates.clear();
    this.initialized = false;
  }

  /**
   * 初始化状态管理
   */
  static markInitialized(): void {
    this.initialized = true;
  }

  static isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 获取模板数量
   */
  static getTemplateCount(): number {
    return this.templates.size;
  }

  /**
   * 调试方法：打印所有模板信息
   */
  static debugPrintAll(): void {
    console.log('=== Template Manager Debug Info ===');
    console.log(`Total templates: ${this.getTemplateCount()}`);
    console.log('Template IDs:', this.getAllTemplateIds());
    console.log('Templates with disabled title:', this.getTemplatesWithDisabledTitle());
    console.log('Templates with disabled customerQuestion:', this.getTemplatesWithDisabledCustomerQuestion());
    console.log('Templates with disabled cardCount:', this.getTemplatesWithDisabledCardCount());
    console.log('=====================================');
  }
}

// 导出类型和管理器
export * from './types';
export { TemplateManager as default };
